import type { DatabaseSyncSnapshot, EntitySyncSnapshot } from 'shared/domain/serviceData/entitySync';

const DATABASE_NAME = 'hustro';
const SERVICE_TABLE_SUFFIX = 'Service';



function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error);
  });
}

/**
 * Opens the existing database without a version: no migrations run and the
 * database is never created (the upgrade a missing database would trigger is
 * aborted). The connection gives way to the service worker's upgrades.
 */
function openExistingDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME);
    request.onupgradeneeded = (): void => request.transaction?.abort();
    request.onsuccess = (): void => {
      const database = request.result;
      database.onversionchange = (): void => database.close();
      resolve(database);
    };
    request.onerror = (): void => reject(request.error);
  });
}

async function readLastRow(store: IDBObjectStore): Promise<unknown> {
  const cursor = await requestToPromise(store.openCursor(null, 'prev'));
  return cursor?.value ?? null;
}

async function readEntitySyncSnapshot(
  transaction: IDBTransaction,
  serviceTable: string,
  entityTable: string | undefined
): Promise<EntitySyncSnapshot> {
  return {
    serviceData: await readLastRow(transaction.objectStore(serviceTable)),
    rowCount: entityTable
      ? await requestToPromise(transaction.objectStore(entityTable).count())
      : null,
  };
}

/**
 * Sync state of every entity, read by the page straight from IndexedDB (same
 * origin as the service worker), so it is available when the worker does not
 * answer: the `ServiceData` row (`isDownloading`, `isDownloaded`, ...) next to
 * the number of rows the entity table actually holds.
 */
export async function readDatabaseSyncSnapshot(): Promise<DatabaseSyncSnapshot> {
  const database = await openExistingDatabase();
  try {
    const tableNames = [...database.objectStoreNames];
    const serviceTables = tableNames.filter((name) =>
      name.endsWith(SERVICE_TABLE_SUFFIX)
    );
    const transaction = database.transaction(tableNames, 'readonly');
    const entries = await Promise.all(
      serviceTables.map(async (serviceTable) => {
        const entity = serviceTable.slice(0, -SERVICE_TABLE_SUFFIX.length);
        const entityTable = tableNames.includes(entity) ? entity : undefined;
        return [
          entity,
          await readEntitySyncSnapshot(transaction, serviceTable, entityTable),
        ] as const;
      })
    );
    return Object.fromEntries(entries);
  } finally {
    database.close();
  }
}
